<template>
  <v-dialog
    :model-value="modelValue"
    :max-width="smAndDown ? undefined : 760"
    :fullscreen="smAndDown"
    :transition="smAndDown ? 'dialog-bottom-transition' : 'dialog-transition'"
    :content-class="
      smAndDown
        ? 'unlock-benefits-dialog unlock-benefits-dialog--mobile'
        : 'unlock-benefits-dialog'
    "
    @update:model-value="onDialogToggle"
  >
    <div class="unlock-benefits">
      <button class="close-btn" type="button" @click="closeDialog">
        <v-icon icon="mdi-close" />
      </button>

      <img class="brand-logo" src="/img/logo-f-123x190.webp" alt="FlixLatino" />

      <h2 class="title">{{ $t("avodDialogs.unlockBenefits.title") }}</h2>
      <p class="subtitle">
        {{ $t("avodDialogs.unlockBenefits.subtitle") }}
      </p>

      <div class="divider"></div>

      <p class="benefits-intro">
        {{ $t("avodDialogs.unlockBenefits.intro") }}
      </p>

      <ul class="benefits-list">
        <li class="benefit-item">
          <img
            class="benefit-icon"
            src="/img/avod/double-film.svg"
            alt=""
            aria-hidden="true"
          />
          <span>{{ $t("avodDialogs.unlockBenefits.benefits.premiumHours") }}</span>
        </li>
        <li class="benefit-item">
          <img
            class="benefit-icon"
            src="/img/avod/tv-icon.svg"
            alt=""
            aria-hidden="true"
          />
          <span>{{ $t("avodDialogs.unlockBenefits.benefits.devices") }}</span>
        </li>
        <li class="benefit-item">
          <img
            class="benefit-icon"
            src="/img/avod/no-ads.svg"
            alt=""
            aria-hidden="true"
          />
          <span>{{ $t("avodDialogs.unlockBenefits.benefits.noAds") }}</span>
        </li>
      </ul>

      <button class="cta-btn" type="button" @click="$emit('start-now')">
        {{ $t("avodDialogs.unlockBenefits.startNow") }}
      </button>

      <button
        v-if="!isLoggedIn"
        class="login-link"
        type="button"
        @click="$emit('login')"
      >
        {{ $t("avodDialogs.unlockBenefits.login") }}
      </button>

      <button v-else class="login-link" type="button" @click="closeDialog">
        {{ $t("avodDialogs.unlockBenefits.notNow") }}
      </button>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useDisplay } from "vuetify";
import { UserStore } from "../../../stores/userStore";

const props = withDefaults(
  defineProps<{
    modelValue?: boolean;
  }>(),
  {
    modelValue: false,
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "close"): void;
  (e: "login"): void;
  (e: "start-now"): void;
}>();

const { smAndDown } = useDisplay();
const userStore = UserStore();
const isLoggedIn = computed(() => !!userStore.getToken);

function onDialogToggle(value: boolean) {
  emit("update:modelValue", value);
  if (!value) emit("close");
}

function closeDialog() {
  emit("update:modelValue", false);
  emit("close");
}
</script>

<style scoped>
.unlock-benefits {
  position: relative;
  width: 760px;
  min-height: 820px;
  border-radius: 48px;
  border: 1px solid transparent;
  background: radial-gradient(
      75% 80% at 30% 20%,
      rgba(57, 66, 118, 0.6) 0%,
      rgba(31, 39, 81, 0.38) 38%,
      rgba(16, 24, 57, 0.9) 100%
    ),
    linear-gradient(180deg, #1c2650 0%, #11183b 100%);
  padding: 64px 56px 52px;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  overflow: hidden;
  box-sizing: border-box;
}

.unlock-benefits::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 3rem;
  padding: 2px;
  background: linear-gradient(
    135deg,
    rgba(158, 120, 255, 0.95) 0%,
    rgba(119, 86, 255, 0.9) 35%,
    rgba(255, 64, 153, 0.9) 70%,
    rgba(255, 64, 153, 0.95) 100%
  );
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  mask-composite: exclude;
  pointer-events: none;
}

.close-btn {
  position: absolute;
  top: 24px;
  right: 24px;
  width: 36px;
  height: 36px;
  background: transparent;
  border: none;
  color: #f2f5ff;
}

.brand-logo {
  width: 82px;
  height: auto;
  margin-top: 18px;
  margin-bottom: 34px;
}

.title {
  margin: 0 0 12px 0;
  max-width: 560px;
  font-size: 2.15rem;
  line-height: 1.15;
  font-weight: 800;
  letter-spacing: 0;
}

.subtitle {
  margin: 0;
  max-width: 500px;
  color: #bac7f3;
  font-size: 1.18rem;
  line-height: 1.5;
}

.divider {
  width: 100%;
  max-width: 300px;
  height: 1px;
  margin: 28px 0 26px;
  background: rgba(138, 154, 205, 0.28);
}

.benefits-intro {
  margin: 0 0 26px 0;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.45;
}

.benefits-list {
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
  max-width: 330px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  text-align: left;
}

.benefit-item {
  display: grid;
  grid-template-columns: 28px 1fr;
  align-items: center;
  gap: 14px;
  color: #ffffff;
  font-size: 1.02rem;
  line-height: 1.35;
}

.benefit-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
  display: block;
}

.cta-btn {
  margin-top: 42px;
  width: 100%;
  max-width: 350px;
  height: 66px;
  border: none;
  border-radius: 18px;
  background: #fa002b;
  color: #fff;
  font-size: 1.1rem;
  font-weight: 900;
}

.login-link {
  margin-top: 22px;
  background: transparent;
  border: none;
  color: #ffffff;
  font-size: 1rem;
  line-height: 1.4;
}

@media (max-width: 959px) {
  :deep(.unlock-benefits-dialog--mobile) {
    margin: 0;
    width: 100vw !important;
    max-width: 100vw !important;
    max-height: 100vh !important;
    height: 100vh !important;
  }

  .unlock-benefits {
    border-radius: 0;
    min-height: 100vh;
    width: 100%;
    padding: 42px 20px 30px;
    justify-content: center;
  }

  .unlock-benefits::before {
    border-radius: 0;
  }

  .close-btn {
    top: 12px;
    right: 12px;
  }

  .brand-logo {
    width: 48px;
    margin-top: 0;
    margin-bottom: 22px;
  }

  .title {
    max-width: 320px;
    font-size: 30px;
    margin-bottom: 10px;
  }

  .subtitle {
    max-width: 320px;
    font-size: 1rem;
  }

  .divider {
    max-width: 220px;
    margin: 24px 0 22px;
  }

  .benefits-intro {
    max-width: 320px;
    font-size: 0.98rem;
    margin-bottom: 22px;
  }

  .benefits-list {
    max-width: 280px;
    gap: 16px;
  }

  .benefit-item {
    grid-template-columns: 24px 1fr;
    gap: 12px;
    font-size: 0.98rem;
  }

  .benefit-icon {
    font-size: 22px;
  }

  .cta-btn {
    max-width: 320px;
    height: 56px;
    border-radius: 16px;
    margin-top: 34px;
    font-size: 1rem;
  }

  .login-link {
    margin-top: 20px;
    font-size: 0.96rem;
  }
}
</style>
